import type { DateTime } from 'luxon'
import { useEffect, useState } from 'react'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import AcceptedBidVolumePtusDataGrid from '@/features/bidding/components/manualAccept/AcceptBidVolumePtusDataGrid'
import BidManualAcceptButtonBar from '@/features/bidding/components/manualAccept/BidManualAcceptButtonBar'
import type { BidType, ManualAcceptFlow } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { useCalculateBidBasisQuery } from '@/features/bidding/hooks/useCalculateBidBasisQuery'
import type { BidMetaData, BidVolumes, BulkFillMode, PtuVolumes } from '@/features/bidding/types/bid'
import { combineOfferedAndForecast } from '@/features/bidding/utils/calculations/calculateBasisForNewBid'
import { calculateBulkFillCapacity } from '@/features/bidding/utils/calculations/calculateBulkFillCapacity'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { getByCountry } from '@/features/bidding/utils/getBidConfig'
import getBidMetaData from '@/features/bidding/utils/getBidMetaData'

interface BidEditFormProps {
  manualAcceptFlow: ManualAcceptFlow
  activationGroup: ActivationGroup
  deliveryDay: MarketDate
  bidType: BidType
  setUnsavedChanges: (unsavedChanges: boolean) => void
}

const BidManualAcceptForm = ({
  manualAcceptFlow,
  activationGroup,
  deliveryDay,
  bidType,
  setUnsavedChanges,
}: BidEditFormProps) => {
  const { selectedCountry } = useBiddingContext()
  const { permissions } = usePermissions()
  const canMutateBidding = permissions.has('biddingMutation')
  const [bidMetaData] = useState<BidMetaData>(() => getBidMetaData(deliveryDay, bidType, activationGroup))
  const [bidPtus, setBidPtus] = useState<PtuVolumes[]>([])
  const { bidBasis, isFetching: isFetchingBidBasis } = useCalculateBidBasisQuery({
    date: bidMetaData.deliveryDay,
    marketProgram: bidMetaData.marketProgram,
    activationGroupUuid: bidMetaData.activationGroupUuid,
    bidType: bidMetaData.bidType,
    selectedCountry,
  })
  const { marketsThatRequirePositiveIntegerVolume } = getByCountry(selectedCountry)

  const fillAllPtus = async (bulkMode: BulkFillMode, bulkFillValue: number | undefined) => {
    if (!bulkFillValue) return

    setBidPtus((prevState) =>
      prevState.map((ptu) => {
        const acceptedCapacityWatts = calculateBulkFillCapacity({
          bulkMode,
          ptu,
          bulkFillValue,
          needsToRoundDown: acceptsOnlyPositiveInteger,
        })
        if (!acceptedCapacityWatts) return ptu

        return { ...ptu, acceptedVolume: { quantity: acceptedCapacityWatts, unit: 'WATTS' } }
      }),
    )
  }

  const processBidPtuUpdate = async (newPtu: PtuVolumes): Promise<PtuVolumes> => {
    const value = Number(newPtu.acceptedVolume?.quantity)
    const isValid = Number.isInteger(value) && value >= 0
    if (!isValid) {
      throw new Error('Accepted capacity value must be a positive integer')
    }

    setBidPtus((prevState) =>
      prevState.map((prevPtu) =>
        prevPtu.ptu.start.equals(newPtu.ptu.start) ? { ...prevPtu, acceptedVolume: newPtu.acceptedVolume } : prevPtu,
      ),
    )
    setUnsavedChanges(true)
    return newPtu
  }

  useEffect(() => {
    const getCurrentAcceptedVolume = (bidBasis: BidVolumes, ptuStart: DateTime) =>
      bidBasis.ptus.find((acceptedPtu) => acceptedPtu.ptu.start.equals(ptuStart))?.acceptedVolume

    const getPrefilledAcceptedVolume = (bidBasis: BidVolumes, ptuStart: DateTime) => {
      const currentAcceptedVolume = getCurrentAcceptedVolume(bidBasis, ptuStart)
      const offeredVolume = bidBasis.ptus.find((offeredPtu) => offeredPtu.ptu.start.equals(ptuStart))?.offeredVolume
      return currentAcceptedVolume || offeredVolume
    }

    if (bidBasis) {
      setBidPtus(
        combineOfferedAndForecast(bidBasis.ptus, bidBasis.forecast).map((ptu) => ({
          ...ptu,
          acceptedVolume: getPrefilledAcceptedVolume(bidBasis, ptu.ptu.start),
          currentAcceptedVolume: getCurrentAcceptedVolume(bidBasis, ptu.ptu.start),
        })),
      )
    }
  }, [bidBasis?.forecast, bidBasis?.ptus])

  const acceptsOnlyPositiveInteger = marketsThatRequirePositiveIntegerVolume.includes(bidMetaData.marketProgram)

  return (
    <div data-testid={`activation-group-bid-${bidMetaData.activationGroupUuid}`}>
      <AcceptedBidVolumePtusDataGrid
        acceptsOnlyPositiveInteger={acceptsOnlyPositiveInteger}
        bidMetaData={bidMetaData}
        loading={isFetchingBidBasis}
        manualAcceptFlow={manualAcceptFlow}
        processRowUpdate={processBidPtuUpdate}
        ptus={bidPtus}
        onFillAll={fillAllPtus}
      />
      {canMutateBidding && (
        <BidManualAcceptButtonBar bidMetaData={bidMetaData} manualAcceptFlow={manualAcceptFlow} ptus={bidPtus} />
      )}
    </div>
  )
}

export default BidManualAcceptForm
